import React, { useEffect, useMemo, useState } from "react"
import { Input, Modal, Select } from "antd"

import { BulkCodeChange, categoryLabelsInUse, setCodeForLabel } from "features/DataImport/components/DatamapEditor/utils/datamapEdits"
import { MAX_CODE } from "features/DataImport/components/DatamapEditor/utils/validateDatamap"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { DatamapDoc } from "types/datamap.types"

interface BulkCodeChangeModalProps {
  open: boolean
  doc: DatamapDoc
  onApply: (result: BulkCodeChange) => void
  onCancel: () => void
}

/** Sets one answer's code in every category question, for example Don't know to 996 to match the scripter's template. */
const BulkCodeChangeModal: React.FC<BulkCodeChangeModalProps> = ({ open, doc, onApply, onCancel }) => {
  const [label, setLabel] = useState<string | undefined>(undefined)
  const [code, setCode] = useState("")

  useEffect(() => {
    if (!open) return
    setLabel(undefined)
    setCode("")
  }, [open])

  const labels = useMemo(() => categoryLabelsInUse(doc), [doc])
  const codeValid = /^\d+$/.test(code) && Number(code) <= MAX_CODE
  // Stored as a plain number, so "0996" becomes 996.
  const newCode = codeValid ? String(Number(code)) : code
  const preview = label !== undefined && codeValid ? setCodeForLabel(doc, label, newCode) : null
  const s = (n: number): string => (n === 1 ? "" : "s")

  return (
    <Modal open={open} title="Change a code in every question" onCancel={onCancel} footer={null} width={720} centered destroyOnHidden>
      <p className="m-0 mb-4 text-sm text-gray-700">
        Sets the code of one answer in every category question that has it. Use it when the data uses another code than the datamap,
        for example 996 for Don't know because the scripter's template does.
      </p>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-gray-700">Answer</span>
          <Select
            showSearch
            optionFilterProp="label"
            className="w-full"
            placeholder="Answer"
            aria-label="Answer"
            value={label}
            onChange={(value: string) => setLabel(value)}
            options={labels.map((entry) => ({
              value: entry.label,
              label: `${entry.label} (${entry.rows} question${s(entry.rows)}, now ${entry.codes.join(", ")})`,
            }))}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-gray-700">New code</span>
          <Input
            aria-label="New code"
            placeholder="New code"
            value={code}
            status={code !== "" && !codeValid ? "error" : undefined}
            onChange={(e) => setCode(e.target.value.trim())}
          />
        </div>
      </div>
      {preview && (
        <div className="mt-4 text-sm text-gray-700">
          <p className="m-0">
            Changes {preview.changed.length} question{s(preview.changed.length)}.
            {preview.unchanged.length > 0
              ? ` ${preview.unchanged.length} already use${preview.unchanged.length === 1 ? "s" : ""} ${newCode}.`
              : ""}
          </p>
          {preview.skipped.length > 0 && (
            <>
              <p className="m-0 mt-2">Left alone, because {newCode} already means something else there:</p>
              <ul className="m-0 max-h-48 overflow-y-auto pl-4">
                {/* Two rows can share a label while the map is being edited, so the key carries the position too. */}
                {preview.skipped.map((skip, i) => (
                  <li key={`${i}:${skip.label}`}>
                    {skip.label} ({newCode} is "{skip.usedBy}")
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-4">
        <DataQButton type="secondary" onClick={onCancel}>
          Cancel
        </DataQButton>
        <DataQButton type="primary" isDisabled={!preview || preview.changed.length === 0} onClick={() => preview && onApply(preview)}>
          Change
        </DataQButton>
      </div>
    </Modal>
  )
}

export default BulkCodeChangeModal
